import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import * as Sentry from '@sentry/react';

import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { getTokenFromFirebase, userHasCustomClaims } from '../firebase/hooks';
import { createClient } from 'graphql-ws';
import moment from 'moment-timezone';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getHasuraConfig } from 'utilities/config';
import { Roles } from 'firebase/interfaces';
import { useAuth } from 'auth/context/AuthContext';
import StorageService from 'utilities/storageService';
import {
  ACTIVE_SUB_ACCOUNT_ID_KEY,
  HASURA_USER_STORAGE_KEY,
  LAST_REQUEST,
} from '../firebase/constants';
import { onError } from '@apollo/client/link/error';
import { LOGOUT } from 'utilities/routes';
import { sentryBreadcrumbLink } from '../sentry';

const { hasuraGraphQlApi, hasuraGraphQlWs } = getHasuraConfig();
const subAccountStorage = new StorageService(ACTIVE_SUB_ACCOUNT_ID_KEY);
const hasuraUserStore = new StorageService(LAST_REQUEST);
const userStorage = new StorageService(HASURA_USER_STORAGE_KEY);

type DataContexType = { client: ApolloClient<NormalizedCacheObject> };

const dataContext = createContext<DataContexType>({} as DataContexType);

export const useDataProvider = () => {
  const ctx = useContext(dataContext);
  if (!ctx.client && process.env.REACT_APP_IS_DEBUG_MODE)
    throw new Error(
      'useDataProvider must be called from within a DataProvider',
    );
  return ctx;
};

type DPType = React.FC<{ children: React.ReactNode; role?: Roles }>;
const timezone = moment.tz.guess();

export const DataProvider: DPType = ({ children }) => {
  const { user } = useAuth();
  const userData = userStorage.getData();

  const userRef = useRef(user);
  userRef.current = user;

  const userDataRef = useRef(userData);
  userDataRef.current = userData;

  useEffect(() => {
    if (userData) {
      Sentry.setUser({
        id: userData.id,
        email: userData.SENSITIVE_email,
        role: userData.role,
      });

      Sentry.setContext('session_data', {
        role: userData.role,
      });
    } else {
      Sentry.setUser(null);
      Sentry.setContext('session_data', null);
    }
  }, [userData]);

  const client = useMemo(() => {
    const httpLink = createHttpLink({
      uri: hasuraGraphQlApi,
      credentials: 'include',
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      if (operation.getContext().ignoreGlobalErrors) {
        return;
      }
      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          Sentry.withScope((scope) => {
            scope.setContext('GraphQL Operation', {
              operationName: operation.operationName,
              variables: operation.variables,
              extensions: err.extensions,
            });
            scope.setTag('graphql.operation_name', operation.operationName);

            scope.setFingerprint([
              '{{ default }}',
              operation.operationName,
              err.message,
            ]);

            if (
              err.message !==
              'Missing "data" field with no errors in response from remote'
            ) {
              Sentry.captureException(
                new Error(`GraphQL Error: ${err.message}`),
              );
            }
          });

          if (
            err.message ===
            'Missing "data" field with no errors in response from remote'
          ) {
            console.warn(err);
            window.location.href = LOGOUT;
          }
          if (err.extensions?.statusCode === 404) {
            // window.location.href = NOT_FOUND_PAGE;
            console.log(
              `[GraphQL Not Found Error]: Message: ${err.message}`,
              err,
              operation,
            );
          }
          if (
            err.extensions?.statusCode === 403 &&
            (err.message === 'Phone number not verified' ||
              err.message === 'Email not verified')
          ) {
            console.warn(
              `[GraphQL Not Found Error]: Message: ${err.message}`,
              err,
              operation,
            );
            window.location.href = LOGOUT;
          }
        }
      }
      if (networkError) {
        Sentry.withScope((scope) => {
          scope.setContext('GraphQL Network Error', {
            operationName: operation.operationName,
            variables: operation.variables,
          });
          scope.setTag('graphql.operation_name', operation.operationName);
          Sentry.captureException(networkError);
        });

        console.error(`[Network error]: ${networkError}`);
      }
    });

    const authLink = setContext(async (_, { headers }) => {
      const token = await getTokenFromFirebase();
      const subAccountId = subAccountStorage.getData();
      hasuraUserStore.setData(moment().toISOString());

      const hasCustomClaims = await userHasCustomClaims();

      const currentUser = userRef.current;
      const currentUserData = userDataRef.current;
      const userRole = currentUser?.role || currentUserData?.role;

      return {
        headers: {
          'Time-Zone': timezone,
          ...(hasCustomClaims && { Authorization: `Bearer ${token}` }),
          ...(userRole && { 'X-Hasura-Role': userRole }),
          ...(subAccountId && { 'x-sub-patient-id': subAccountId }),
          ...headers,
        },
      };
    });
    const wsLink = new GraphQLWsLink(
      createClient({
        url: hasuraGraphQlWs,
        keepAlive: 60000,
        retryAttempts: Infinity,
        shouldRetry: (errOrCloseEvent) => {
          console.log(
            '[GraphQL WS] Attempting to reconnect...',
            errOrCloseEvent,
          );
          return true;
        },
        on: {
          connected: () => console.log('[GraphQL WS] Connected.'),
          error: (err) => console.warn('[GraphQL WS] Error:', err),
          closed: (event) => console.warn('[GraphQL WS] Closed:', event),
        },
        connectionParams: async () => {
          const token = await getTokenFromFirebase();
          const hasCustomClaims = await userHasCustomClaims();
          const subAccountId = subAccountStorage.getData();

          return {
            headers: {
              ...(hasCustomClaims && { Authorization: `Bearer ${token}` }),
              ...(subAccountId && { 'x-sub-patient-id': subAccountId }),
            },
          };
        },
      }),
    );

    const httpLinkChain = sentryBreadcrumbLink.concat(
      errorLink.concat(authLink.concat(httpLink)),
    );

    const link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLinkChain,
    );

    return new ApolloClient({
      link,
      cache: new InMemoryCache({
        typePolicies: {
          HasuraMedicationItem: {
            keyFields: ['id', 'responseType', 'value'],
          },
        },
      }),
    });
  }, []);

  return (
    <ApolloProvider client={client}>
      <dataContext.Provider value={{ client }}>{children}</dataContext.Provider>
    </ApolloProvider>
  );
};
